import { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { subDays, format, isValid, parse, isAfter, startOfDay } from 'date-fns';

const REQUIRED_KEYS = ['start', 'end'];

export const dateFormatStr = 'yyyy-MM-dd';

export const validateParams = (params: Record<string, string>) => {
  const { start, end } = params;

  const hasAllKeys = REQUIRED_KEYS.every(key => key in params);
  if (!hasAllKeys) return false;

  const startDate = parse(start, dateFormatStr, new Date());
  const endDate = parse(end, dateFormatStr, new Date());

  if (!isValid(startDate) || !isValid(endDate)) return false;

  return !isAfter(startDate, endDate);
};

export const getDefaultDates = (days: number) => {
  const now = new Date();
  return {
    start: format(subDays(startOfDay(now), days), dateFormatStr),
    end: format(now, dateFormatStr)
  };
};

export const useHashParams = () => {
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => (Object.fromEntries(new URLSearchParams(hash.substring(1)))), [hash]);

  const setHashParams = useCallback((newParams: Record<string, string | number>) => {
    const currentParams = new URLSearchParams(hash.substring(1));

    Object.entries(newParams).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        currentParams.delete(key);
      } else {
        currentParams.set(key, String(value));
      }
    });

    navigate(`${pathname}#${currentParams.toString()}`, { replace: true });
  }, [hash, navigate, pathname]);

  return [params, setHashParams] as const;
};